import { Card, Container, Layout, Loader } from "@certn/cascadia-react";
import { Text, Stack } from "@certn/cascadia";
import { css } from "@emotion/css";
import { useClientPortalAPI } from "@certn/api";
import { useAuth } from "@certn/auth";
import { getRegionFromUrl, isSupportedRegion } from "@certn/utils";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";

type SsoConfigPath = "/api/internal/client-portal/sso-config/";

export function SsoSplashPage() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const connectionName =
    searchParams.get("connectionName") ?? searchParams.get("connection");
  const { loginWithRedirect } = useAuth();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Construct the API path with query param, or null to skip the fetch
  const apiPath = connectionName
    ? (`/api/internal/client-portal/sso-config/?connection_name=${connectionName}` as SsoConfigPath)
    : null;

  const { isLoading } = useClientPortalAPI(apiPath, undefined, {
    // Disable SWR revalidation - this is a one-time check that triggers a redirect on success.
    // We don't want refetches on focus/reconnect which could cause duplicate redirects.
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
    revalidateIfStale: false,
    onSuccess: (data) => {
      if (data.sso_enabled && data.connection_name) {
        const urlRegion = getRegionFromUrl();
        const regionalReturnTo = isSupportedRegion(urlRegion)
          ? `/${urlRegion}/`
          : "/";

        loginWithRedirect({
          authorizationParams: {
            connection: data.connection_name,
          },
          appState: {
            returnTo: regionalReturnTo,
          },
        });
      } else if (!data.sso_enabled) {
        setErrorMessage(t("SSO is not enabled for this organization"));
      } else if (!data.connection_name) {
        setErrorMessage(t("SSO configuration is incomplete"));
      }
    },
    onError: (error: Error) => {
      setErrorMessage(error.message);
    },
  });

  useEffect(() => {
    if (!connectionName) {
      setErrorMessage(
        t("Missing required parameter: connectionName or connection")
      );
    } else {
      setErrorMessage(null);
    }
  }, [connectionName, t]);

  return (
    <Layout layout="auth">
      <Container
        padding={["space-3"]}
        layout="applicant/narrow"
        testID="sso-splash-page-container"
      >
        <Card className={styles.card}>
          {isLoading ? (
            <Loader overlay />
          ) : (
            <Stack gap="space-4">
              {errorMessage && (
                <Text style="global/body-primary" color="text-danger">
                  {errorMessage}
                </Text>
              )}
            </Stack>
          )}
        </Card>
      </Container>
    </Layout>
  );
}

const styles = {
  card: css({
    padding: "var(--space-4)",
    display: "flex",
    flexDirection: "column",
    gap: "var(--space-4)",
    borderRadius: "var(--border-radius-3)",
  }),
};
